/**
 * 페이지 액션 추적
 * 
 * 인페이지에서 발생하는 다양한 상호작용을 추적합니다.
 * action_key로부터 action_group을 자동으로 추론합니다.
 * 
 * @example
 * ```typescript
 * import { trackPageAction } from 'lib/analytics/core/actions';
 * 
 * // 토글
 * trackPageAction('toggle_won');              // action_group: 'toggle'
 * trackPageAction('toggle_stock_fundamental');
 * 
 * // 필터
 * trackPageAction('filter_sector');           // action_group: 'filter'
 * trackPageAction('filter_feed_stock');
 * 
 * // 정렬
 * trackPageAction('sort_watchlist');          // action_group: 'sort'
 * 
 * // 범위 선택
 * trackPageAction('range_stock_chart');       // action_group: 'range'
 * 
 * // 확장/축소
 * trackPageAction('expand_stock_fundamental'); // action_group: 'expand'
 * trackPageAction('collapse_stock_info');      // action_group: 'collapse'
 * 
 * // 복사/공유/내보내기
 * trackPageAction('copy_link');                // action_group: 'copy'
 * trackPageAction('share_planner_result');     // action_group: 'share'
 * trackPageAction('export_pf_ts_score');       // action_group: 'export'
 * 
 * // 선택/입력/슬라이더/이동/체크
 * trackPageAction('select_pf_survey');         // action_group: 'select'
 * trackPageAction('input_pf_price');           // action_group: 'input'
 * trackPageAction('slider_brief');             // action_group: 'slider'
 * trackPageAction('move_strat_round');         // action_group: 'move'
 * trackPageAction('check_pf_curprice');        // action_group: 'check'
 * trackPageAction('flip_pf_ts_risk');          // action_group: 'flip'
 * ```
 */

import { track } from './client';
import type { PageActionProps } from './schema';

type ActionGroup = PageActionProps['action_group'];

/**
 * 페이지 액션 추적
 * 
 * @param actionKey - 액션 키 (예: filter_sector, toggle_won, sort_watchlist)
 * @param actionGroup - 액션 그룹 (자동 추론 가능, 명시적 지정도 가능)
 */
export function trackPageAction(
    actionKey: string,
    actionGroup?: ActionGroup
): void {
    const inferredGroup = actionGroup || inferActionGroup(actionKey);

    track('page_action', {
        action_group: inferredGroup,
        action_key: actionKey,
    });
}

/**
 * action_key의 prefix로부터 action_group 추론
 * 
 * @param actionKey - 액션 키
 * @returns 추론된 action_group
 */
function inferActionGroup(actionKey: string): ActionGroup {
    // prefix 기반 매칭 (순서 중요: 긴 패턴부터 체크)
    if (actionKey.startsWith('toggle_')) return 'toggle';
    if (actionKey.startsWith('filter_')) return 'filter';
    if (actionKey.startsWith('sort_')) return 'sort';
    if (actionKey.startsWith('range_')) return 'range';
    if (actionKey.startsWith('expand_')) return 'expand';
    if (actionKey.startsWith('collapse_')) return 'collapse';
    if (actionKey.startsWith('copy_')) return 'copy';
    if (actionKey.startsWith('share_')) return 'share';
    if (actionKey.startsWith('export_')) return 'export';
    if (actionKey.startsWith('select_')) return 'select';
    if (actionKey.startsWith('input_')) return 'input';
    if (actionKey.startsWith('slider_')) return 'slider';
    if (actionKey.startsWith('move_')) return 'move';
    if (actionKey.startsWith('check_')) return 'check';
    if (actionKey.startsWith('flip_')) return 'flip';

    // fallback: 첫 번째 언더스코어 이전 문자열 사용
    const prefix = actionKey.split('_')[0];
    console.warn(
        `[Analytics] Unknown action_key prefix: "${prefix}" in "${actionKey}". Defaulting to 'select'.`
    );
    return 'page_action'; // 기본값
}

